import { LiftInterface } from "./Lift.interface";
import { BankFloorInterface } from "./Bank.interface";
import { Usage } from "../flatbuffers/generated/usage/usage";
import { Control } from "../flatbuffers/generated/control/control";
import PositionCallsStatusT from "../types/PositionCallsStatus";
import { FaultMessageT } from "../flatbuffers/generated/fault/fault-message";
import { ServiceMessageT } from "../flatbuffers/generated/service/service-message";
import { UuidT } from "../flatbuffers/generated/front-end/uuid";
import { UiCmdT } from "../flatbuffers/generated/ui-cmd/ui-cmd";
import { DeviceDirection } from "../types/DeviceDirection";
import { EncodedMessageContent } from "../flatbuffers/generated/ln2/encoded-message-content";
import { MessageContent } from "../flatbuffers/generated/ln2/message-content";
import { BitMaskMessageT } from "../flatbuffers/generated/ln2/bit-mask-message";
import { OneHotMessageT } from "../flatbuffers/generated/ln2/one-hot-message";
import { UsageMap } from "../types/monitoringTypes";
import { OneHotEntry } from "../utils/stateMapHelpers";
import { AckOutcome } from "../flatbuffers/generated/front-end/ack-outcome";
import { AckVerified } from "../flatbuffers/generated/front-end/ack-verified";

export type TerminalFilterType = "ALL" | "UNIT" | "BANK";

export type DoorSelectedType = "FRONT" | "REAR" | "NA";
export type LockUnlockType = "lck" | "unlck" | "NA";
export type UpDownType = "UP" | "DOWN" | "NA";
export type CarCallHallCallType = "CARCALL" | "HALLCALL" | "VIP" | "NA";
export type EmulatorMessageType =
  "CONTROL" | "CARCALL" | "HALLCALL" | "VIP" | "VIP_RESET" | "COMMAND";
/** @deprecated Use DeviceDirection from types/DeviceDirection instead */
export type SocketDirection = DeviceDirection;

export const VALID_MESSAGES_NO_ICON = ["PF", "FAULT"];
export const VALID_TYPES = ["FAULT", "CARSERVICE", "TRAFFIC", "STATUS"];

export enum MonitoringViewTypeEnum {
  EXPANDED_VIEW,
  OVER_HEAD_VIEW,
  STATUS_VIEW,
  DEVICE_STATUS_VIEW,
  MAP_VIEW,
}
export interface ControlPanelComponentRearFrontRadioGroupInterface {
  doorSelected: DoorSelectedType;
  setDoorSelected: (value: DoorSelectedType) => void;
}

export interface ControlPanelBankFloors {
  id: string;
  name: string;
  floorNo: number;
  frontDoor: boolean;
  rearDoor: boolean;
}

export interface ControlPanelBankVIPInterface {
  hasFrontDoors: boolean;
  hasRearDoors: boolean;
  bankFloors: ControlPanelBankFloors[];
}

export interface postCommandEdgeCarCallFlatBufferInterface {
  frontRearDoorType: DoorSelectedType;
  lockUnlockType: LockUnlockType;
  isSecureCalls: boolean;
  floor: number;
  isCmdOff: boolean;
}

export interface postCommandEdgeHallCallFlatBufferInterface {
  frontRearDoorType: DoorSelectedType;
  lockUnlockType: LockUnlockType;
  upDownType: UpDownType;
  isSecureCalls: boolean;
  floor: number;
  isCmdOff: boolean;
}

export interface postCommandEdgeCMDInterface {
  isAdapter: boolean;
  isCmdOff: boolean;
  cmdMessage: string;
  carCallHallCallType: CarCallHallCallType;
}

export interface HallCallsState {
  up: PositionCallsStatusT;
  down: PositionCallsStatusT;
  upAssigned: PositionCallsStatusT;
  downAssigned: PositionCallsStatusT;
}

export interface DoorState {
  frontOpen: boolean;
  rearOpen: boolean;
  frontStalled: boolean;
  rearStalled: boolean;
}

export interface IconInterface {
  type: string;
  name: string;
  description: string;
}

/**
 * What VTS's acknowledgement correlator concluded about a command.
 *
 * Rides on every `FrontEndMessage` and is `None`/`Unknown`/`0` on almost all of
 * them, because almost no message is an acknowledgement. It is carried here
 * rather than re-derived because the conclusion cannot be re-derived: deciding
 * whether a controller answered requires remembering what was SENT, and only
 * the correlator does. An answer echoes an ADDRESS, and the same address means
 * one thing written and another reported -- on the Brigham adapter
 * `Control::SecureCarCall` is written into the block where `Traffic::P` reports
 * car position -- so a reader watching the stream cannot tell an answer from a
 * coincidence.
 */
export interface AckVerdict {
  /**
   * `AckOutcome` as the schema numbers it: 0 None, 1 Matched, 2 Unsolicited,
   * 3 Expired, 4 Withheld.
   *
   * Withheld is the odd one out: VTS never sent the command at all, because a
   * channel modifier with `timeout_policy` DROP gated it and the gate failed (the
   * interlock did not confirm, or its payload could not be delivered). The frame
   * repeats the command that was held back, so it is NOT device state — the
   * reducer records only the verdict and a `WithheldMark`, never the command.
   */
  outcome: AckOutcome;
  /** `AckVerified`: 0 Unknown, 1 Yes, 2 No. Tri-state because "not checked" and
   * "checked and wrong" are different facts. */
  verified: AckVerified;
  /** How long the controller took, in milliseconds. 0 when not applicable. For a
   * Withheld verdict it is how long the gate waited before giving up. */
  elapsedMs: number;
}

/** The verdict every message that is NOT an acknowledgement carries. */
export const NO_ACK_VERDICT: AckVerdict = {
  outcome: AckOutcome.None,
  verified: AckVerified.Unknown,
  elapsedMs: 0,
};

/**
 * One elevator command VTS withheld on a floor: a channel-modifier gate with
 * `timeout_policy` DROP failed, so the command was never sent.
 *
 * Kept on the lift state so the floor cell can show that the operator's press
 * was held back rather than lost, and so the notice hook can name what was held.
 */
export interface WithheldMark {
  /** The floor index (`position`) the withheld Elevator Control command addressed. */
  position: number;
  /** The command that was held back, e.g. `Control.SecureCarCall`. */
  control: Control;
  /** When VTS issued the verdict: the frame timestamp in epoch milliseconds. */
  atMs: number;
}

export interface SocketMessageInterface {
  bankId: UuidT;
  liftId: UuidT;
  carCalls: PositionCallsStatusT;
  hallCalls: HallCallsState;
  faults: boolean;
  activeFaults: FaultMessageT[];
  position: number | null;
  serviceMode: ServiceMessageT | null;
  direction: DeviceDirection;
  doorStatus: DoorState;
  doorZone: Usage | null;
  levelling: boolean;
  escalatorControl: Control | null;
  connected: boolean;
  msg: {
    contentType: EncodedMessageContent;
    messageType: MessageContent;
    content: BitMaskMessageT | OneHotMessageT | null;
    active: boolean;
  };
  /** Typed usage state keyed by Usage enum values. */
  usage: UsageMap;
  bitmask: { [key: string]: boolean };
  positionMap: { [key: string]: number };
  serviceMap: { [key: string]: string };
  oneHotMap: { [key: string]: OneHotEntry };
  /**
   * The acknowledgement verdict this message reports, if it reports one.
   *
   * Defaults to [`NO_ACK_VERDICT`], which is what every ordinary telemetry
   * message carries.
   */
  ack: AckVerdict;
  /**
   * Commands VTS withheld on this lift in the last `WITHHELD_MARK_TTL_MS`, one
   * mark per Withheld verdict for an Elevator Control command. A mark is pruned
   * once it is older than the TTL, and cleared for its floor as soon as a car
   * call registers there. Empty on every state that has seen no such verdict.
   */
  withheldCalls: WithheldMark[];
}

/**
 * Everything the Terminal knows about an OUTBOUND UiCmd frame, derived by
 * decoding the bytes that were actually handed to `WebSocket.send` rather than
 * by echoing the pre-send request object. `uiCmd` is the generated Object API
 * value produced by `UiCmd.getRootAsUiCmd(...).unpack()`.
 */
export interface TerminalTxMeta {
  uiCmd: UiCmdT;
  /** Send-ordering number stamped by the TX bus. Unique per frame. */
  seq: number;
  /** Control variant name carried by the command, e.g. "EnergizeCarCall". */
  commandName: string;
  /** Space-separated lowercase hex of every sent byte. */
  hex: string;
  byteLength: number;
  sentAt: number;
  serverId: string;
  serverName: string;
  channelId: string;
}

/**
 * A row in the Terminal message table. Inbound rows are plain
 * `SocketMessageInterface` values off the WebSocket; outbound rows carry
 * `txMeta` as well, which is what the direction column keys off. The absence
 * of `txMeta` IS the "this came from the controller" signal — there is no
 * separate discriminant to keep in sync.
 */
export type TerminalRowInterface = SocketMessageInterface & { txMeta?: TerminalTxMeta };

export interface DeviceViewDataByServerInterface {
  idServer: string;
  nameServer: string;
  lifts: { id: string; name: string; bank: string }[];
  liftsChunks: { id: string; name: string; bank: string }[][];
}

export interface DeviceViewDataByBankInterface {
  idBank: string;
  nameBank: string;
  lifts: { id: string; name: string }[];
  liftsChunks: { id: string; name: string }[][];
}

export interface MonitoringDeviceConfigurationInterface {
  showElevators: boolean;
  showEscalators: boolean;
  showMovingwalks: boolean;
  showGpio: boolean;
  viewType: "server" | "bank";
}

export interface LayoutInterface {
  value: string;
  label: string;
  children: any[];
  type: string;
  fbEnabled: boolean;
  idOrganization: string;
  idServer: string;
  idChannel: string;
  idBank: string;
  nameOrganization: string;
  nameServer: string;
  nameChannel: string;
  typeChannel: string;
  id: string;
  createdAt?: string;
  updatedAt?: string;
  deletedAt?: string | null;
  active: boolean;
  floorBelowLobby: number;
  floorCount: number;
  logicalPort: number;
  name: string;
  numberOfLifts: number;
  channelId: string;
  rearDoors: boolean;
  stops: number;
  control_panel: any;
  bankFloors: BankFloorInterface[];
  lifts: LiftInterface[];
  floorsInUse: any[];
  i: string;
  x: number;
  y: number;
  w: number;
  h: number;
  l: number;
  f: number;
  floor: number;
  actions?: any;
  startFloorNo: number;
}

export interface TerminalGlobalObject {
  id: string;
  name: string;
  channelId: string;
  serverId: string;
  liftId: string;
  bankId: string;
}

export interface FaultConfigurationInterface {
  showCounter: boolean;
  rules: { name: string; counter: number }[];
}

export type FaultConfigInput = Partial<FaultConfigurationInterface> | null | undefined;

export interface FlatBufferMessage {
  contentType: number;
  messageType: number;
  message: number;
}
